/* ==========================================================================
   MX Footer
   One custom element, <mx-footer>, per section instance. Everything it starts in connectedCallback is
   stopped in disconnectedCallback, so Theme Editor re-renders leave nothing behind.

   The footer is complete without this file: every link, the newsletter form and the country/language
   form work as plain HTML. The script adds:
     • phone accordions for the menus (the inline script in the section marks the footer "is-js" before
       first paint; if this file never arrives that mark is removed again and the menus are plain lists);
     • the same reading order on every screen when a different phone order is chosen;
     • a clear message for a mistyped email before the form is sent;
     • closing the country/language panel with Escape or a click elsewhere;
     • placing the store card over or under the map, and the optional Maps JavaScript API map;
     • the entrance animation.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('mx-footer')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var wide = window.matchMedia('(min-width: 750px)'); /* tablet and desktop: the layout is fixed */
  var AREAS = ['brand', 'menus', 'map', 'news'];
  var PARTS = ['locale', 'copy', 'pay'];
  var mapsLoading = null; /* one Maps JavaScript API load for the whole page */

  function qsa(sel, ctx) { return Array.prototype.slice.call(ctx.querySelectorAll(sel)); }
  /* MediaQueryList.addEventListener is missing in Safari before 14 */
  function onMedia(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn); else if (mq.addListener) mq.addListener(fn);
    return function () { if (mq.removeEventListener) mq.removeEventListener('change', fn); else if (mq.removeListener) mq.removeListener(fn); };
  }
  function ms(el, prop, fallback) {
    var v = getComputedStyle(el).getPropertyValue(prop).trim();
    var n = parseFloat(v);
    if (!v || isNaN(n)) return fallback;
    return /ms$/.test(v) ? n : n * 1000;
  }
  /* Loads Google's Maps JavaScript API once. Resolves with google.maps, rejects if it cannot be loaded. */
  function loadMaps(key) {
    if (window.google && window.google.maps && (window.google.maps.Map || window.google.maps.importLibrary)) return Promise.resolve(window.google.maps);
    if (mapsLoading) return mapsLoading;
    mapsLoading = new Promise(function (resolve, reject) {
      var done = false;
      window.__mxfMapsReady = function () { done = true; resolve(window.google.maps); };
      /* Google calls this when the key is refused */
      var previous = window.gm_authFailure;
      window.gm_authFailure = function () { if (typeof previous === 'function') previous(); document.dispatchEvent(new CustomEvent('mx:footer:map-failed')); };
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&v=weekly&loading=async&callback=__mxfMapsReady';
      s.async = true;
      s.onerror = function () { if (!done) { mapsLoading = null; reject(new Error('Maps JavaScript API could not be loaded')); } };
      document.head.appendChild(s);
    });
    return mapsLoading;
  }

  class MXFooter extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (this.started) return;
      this.cleanup = [];
      this.timers = {};
      this.start();
      /* if something made this script run before the section's markup was parsed, start once it is */
      if (!this.started && document.readyState === 'loading') {
        this.listen(document, 'DOMContentLoaded', function () { if (!self.started) self.start(); });
      }
    }
    start() {
      if (!this.querySelector('.mxf__inner')) return;
      this.started = true;
      /* the inline script's "main script never arrived" fallback is no longer needed */
      if (this.mxfPreTimer) { clearTimeout(this.mxfPreTimer); this.mxfPreTimer = 0; }
      this.classList.add('is-js', 'is-ready');
      this.initAccordions();
      this.initOrder();
      this.initNewsletter();
      this.initLocale();
      this.initSignals();
      this.initMap();
      this.initEntrance();
      this.initEditor();
    }
    disconnectedCallback() {
      var self = this;
      (this.cleanup || []).forEach(function (fn) { fn(); });
      this.cleanup = [];
      Object.keys(this.timers || {}).forEach(function (k) { clearTimeout(self.timers[k]); });
      this.timers = {};
      if (this.fitRaf) { cancelAnimationFrame(this.fitRaf); this.fitRaf = 0; }
      ['resizeObserver', 'mapObserver', 'entranceObserver'].forEach(function (k) {
        if (self[k]) { self[k].disconnect(); self[k] = null; }
      });
      this.started = false;
    }
    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.cleanup.push(function () { target.removeEventListener(type, fn, opts); });
    }
    timer(name, fn, delay) {
      clearTimeout(this.timers[name]);
      this.timers[name] = setTimeout(fn, delay);
    }

    /* ------------------------------------------------------------ menus as accordions (phones) */
    /* The buttons only show on phones (the stylesheet decides), so nothing here needs to know the
       screen size: on wider screens the plain headings are shown and every list is open. */
    initAccordions() {
      var self = this;
      this.listen(this, 'click', function (e) {
        var toggle = e.target.closest && e.target.closest('[data-mxf-toggle]');
        if (!toggle || !self.contains(toggle)) return;
        var menu = toggle.closest('[data-mxf-menu]');
        if (menu) self.setMenu(menu, !menu.classList.contains('is-open'));
      });
    }
    setMenu(menu, open) {
      var self = this;
      if (open && this.dataset.accMode !== 'multiple') {
        qsa('[data-mxf-menu].is-open', this).forEach(function (other) { if (other !== menu) self.setMenu(other, false); });
      }
      menu.classList.toggle('is-open', open);
      var toggle = menu.querySelector('[data-mxf-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* closing a long list above can carry the heading just opened off the top of the screen: bring it back */
      if (open && toggle) {
        this.timer('reveal', function () {
          if (document.contains(toggle) && toggle.offsetParent !== null && toggle.getBoundingClientRect().top < 0) toggle.scrollIntoView({ block: 'start' });
        }, 360);
      }
    }

    /* ------------------------------------------------------------ one reading order per screen */
    /* The page is written in the order chosen for phones. On wider screens the areas are placed by the
       grid, wherever they are in the page, so there the page order is put back to the order they are
       seen in: nothing moves on screen, and keyboard and screen-reader order match what is shown. */
    initOrder() {
      var self = this;
      var apply = function () { self.applyOrder(); };
      this.cleanup.push(onMedia(wide, apply));
      apply();
    }
    applyOrder() {
      var main = this.querySelector('[data-mxf-main]');
      var bottom = this.querySelector('[data-mxf-bottom]');
      var phone = !wide.matches;
      if (main) this.reorder(main, 'data-mxf-area', phone ? String(this.dataset.order || '').split(',') : AREAS, 'map');
      if (bottom) this.reorder(bottom, 'data-mxf-part', phone ? String(this.dataset.bottomOrder || '').split(',') : PARTS, '');
    }
    reorder(parent, attr, order, keep) {
      var items = Array.prototype.filter.call(parent.children, function (el) { return el.hasAttribute(attr); });
      var byName = {};
      items.forEach(function (el) { byName[el.getAttribute(attr)] = el; });
      var wanted = order.map(function (name) { return byName[name]; }).filter(Boolean);
      items.forEach(function (el) { if (wanted.indexOf(el) < 0) wanted.push(el); });
      var same = wanted.length === items.length && wanted.every(function (el, i) { return el === items[i]; });
      if (same) return;
      var active = document.activeElement;
      /* the map holds a frame that would load again if it were moved: the others are moved around it */
      var anchor = keep && byName[keep] ? byName[keep] : null;
      if (anchor) {
        var at = wanted.indexOf(anchor);
        wanted.slice(0, at).forEach(function (el) { parent.insertBefore(el, anchor); });
        var ref = anchor;
        wanted.slice(at + 1).forEach(function (el) { parent.insertBefore(el, ref.nextSibling); ref = el; });
      } else {
        wanted.forEach(function (el) { parent.appendChild(el); });
      }
      if (active && active !== document.body && parent.contains(active) && document.activeElement !== active) {
        try { active.focus({ preventScroll: true }); } catch (err) { /* noop */ }
      }
    }

    /* ------------------------------------------------------------ newsletter */
    /* The form is sent to Shopify as it is. The script only stops an address that is plainly not an
       email before it is sent, and says so; success and server errors are printed by the page itself. */
    initNewsletter() {
      var self = this;
      var form = this.querySelector('.mxf-news__form');
      if (!form) return;
      var input = form.querySelector('[data-mxf-email]');
      var box = form.querySelector('[data-mxf-news-msg]');
      var field = form.querySelector('[data-mxf-field]');
      if (!input || !box) return;
      form.setAttribute('novalidate', '');
      var looksValid = function () { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value.trim()); };
      var clear = function () {
        var note = box.querySelector('[data-mxf-client], .mxf-msg--error');
        if (note) note.remove();
        input.removeAttribute('aria-invalid');
        if (field) field.classList.remove('is-invalid');
      };
      this.listen(form, 'submit', function (e) {
        if (form.hasAttribute('data-sending')) { e.preventDefault(); return; }
        if (!looksValid()) {
          e.preventDefault();
          box.textContent = '';
          var p = document.createElement('p');
          p.className = 'mxf-msg mxf-msg--error';
          p.setAttribute('role', 'alert');
          p.setAttribute('data-mxf-client', '');
          var span = document.createElement('span');
          span.textContent = box.getAttribute('data-invalid') || 'Enter a valid email address.';
          p.appendChild(span);
          box.appendChild(p);
          input.setAttribute('aria-invalid', 'true');
          if (field) field.classList.add('is-invalid');
          input.focus();
          return;
        }
        /* One request per press. If another script (a spam check, a marketing app) takes the submit over,
           the form is left free; and the mark never outlives a few seconds. */
        form.setAttribute('data-sending', '');
        self.timer('sending', function () {
          if (e.defaultPrevented) { form.removeAttribute('data-sending'); return; }
          self.timer('sent', function () { form.removeAttribute('data-sending'); }, 8000);
        }, 0);
      });
      this.listen(input, 'input', function () { if (input.getAttribute('aria-invalid') === 'true' && looksValid()) clear(); });
      /* coming back with the browser's Back button: the form can be sent again */
      this.listen(window, 'pageshow', function () { form.removeAttribute('data-sending'); });

      /* After sending, Shopify returns to this page with the form's id after the #. Every customer form
         on the page is told "posted", so a message here is only kept if this is the form that was sent. */
      var posted = box.querySelector('[data-mxf-posted]');
      if (posted) {
        var hash = '';
        try { hash = decodeURIComponent((window.location.hash || '').slice(1)); } catch (err) { hash = ''; }
        var other = hash && hash !== form.id ? document.getElementById(hash) : null;
        if (other && other.tagName === 'FORM' && other.querySelector('[name="contact[email]"]')) {
          posted.remove();
          clear();
        } else if (hash === form.id) {
          /* move focus to the message so it is read out; after the browser has finished going to the #,
             which would otherwise take the focus away again */
          var announce = function () {
            self.timer('announce', function () {
              if (!document.contains(posted)) return;
              try { posted.focus({ preventScroll: false }); } catch (err) { posted.focus(); }
            }, 60);
          };
          if (document.readyState === 'complete') announce(); else this.listen(window, 'load', announce);
        }
      }
    }

    /* ------------------------------------------------------------ country and language */
    initLocale() {
      var self = this;
      var details = this.querySelector('[data-mxf-locale]');
      if (!details) return;
      var summary = details.querySelector('summary');
      this.listen(document, 'click', function (e) { if (details.open && !details.contains(e.target)) details.open = false; });
      this.listen(details, 'keydown', function (e) {
        if (e.key !== 'Escape' || !details.open) return;
        e.stopPropagation();
        details.open = false;
        if (summary) summary.focus();
      });
      this.listen(details, 'focusout', function (e) {
        if (details.open && e.relatedTarget && !details.contains(e.relatedTarget)) details.open = false;
      });
    }

    /* ------------------------------------------------------------ support cards that signal a chat tool */
    /* A "signal" card sends mx:footer:action with its name. A script that opens a chat tool listens for
       it and calls event.preventDefault(); if nothing does, a card with a link simply opens its link. */
    initSignals() {
      var self = this;
      this.listen(this, 'click', function (e) {
        var card = e.target.closest && e.target.closest('[data-mxf-signal]');
        if (!card || !self.contains(card)) return;
        var event = new CustomEvent('mx:footer:action', { bubbles: true, cancelable: true, detail: { action: card.getAttribute('data-mxf-signal'), trigger: card } });
        if (!card.dispatchEvent(event)) e.preventDefault();
      });
    }

    /* ------------------------------------------------------------ map */
    initMap() {
      var self = this;
      var map = this.querySelector('[data-mxf-map]');
      if (!map) return;
      this.mapEl = map;
      this.storeEl = map.querySelector('[data-mxf-store]');
      this.frameEl = map.querySelector('.mxf-map__frame');
      if (this.storeEl && this.frameEl && map.classList.contains('mxf-map--overlay')) {
        var fit = function () { self.queueFit(); };
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(fit);
          this.resizeObserver.observe(map);
          this.resizeObserver.observe(this.storeEl);
        } else {
          this.listen(window, 'resize', fit);
        }
        this.fitStore();
      }
      var canvas = map.querySelector('[data-mxf-map-canvas]');
      if (canvas) {
        this.listen(document, 'mx:footer:map-failed', function () { self.mapFailed(); });
        if ('IntersectionObserver' in window) {
          this.mapObserver = new IntersectionObserver(function (entries) {
            if (!entries.some(function (en) { return en.isIntersecting; })) return;
            self.mapObserver.disconnect();
            self.mapObserver = null;
            self.startApiMap(canvas);
          }, { rootMargin: '400px 0px' });
          this.mapObserver.observe(canvas);
        } else {
          this.startApiMap(canvas);
        }
      }
    }
    queueFit() {
      var self = this;
      if (this.fitRaf) return;
      this.fitRaf = requestAnimationFrame(function () { self.fitRaf = 0; self.fitStore(); });
    }
    /* The card may sit over the map only if it leaves most of the map, and Google's own controls, free.
       A card made tall by a long address goes underneath instead. */
    fitStore() {
      var map = this.mapEl, store = this.storeEl, frame = this.frameEl;
      if (!map || !store || !frame) return;
      var was = map.classList.contains('is-stacked');
      map.classList.remove('is-stacked');
      var over = getComputedStyle(store).position === 'absolute';
      var cs = getComputedStyle(this);
      var clear = parseFloat(cs.getPropertyValue('--mxf-store-clear-b')) || 0;
      var tooTall = over && store.offsetHeight + clear + 12 > frame.offsetHeight * 0.5;
      if (tooTall) map.classList.add('is-stacked');
      if (was !== tooTall) map.setAttribute('data-store', tooTall ? 'below' : 'over');
    }
    startApiMap(canvas) {
      var self = this;
      var lat = parseFloat(canvas.getAttribute('data-lat'));
      var lng = parseFloat(canvas.getAttribute('data-lng'));
      var key = canvas.getAttribute('data-key') || '';
      if (!key || !(lat >= -90 && lat <= 90) || !(lng >= -180 && lng <= 180)) { this.mapFailed(); return; }
      loadMaps(key).then(function (maps) {
        if (!self.started || !document.contains(canvas)) return;
        var libraries = typeof maps.importLibrary === 'function' ? Promise.all([maps.importLibrary('maps'), maps.importLibrary('marker')]) : Promise.resolve([maps, maps]);
        return libraries.then(function (libs) {
          if (!self.started || !document.contains(canvas)) return;
          var MapClass = (libs[0] && libs[0].Map) || maps.Map;
          var MarkerClass = (libs[1] && libs[1].Marker) || maps.Marker;
          var position = { lat: lat, lng: lng };
          var map = new MapClass(canvas, {
            center: position,
            zoom: parseInt(canvas.getAttribute('data-zoom'), 10) || 15,
            colorScheme: canvas.getAttribute('data-scheme') || 'DARK',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false
          });
          if (MarkerClass) new MarkerClass({ position: position, map: map, title: canvas.getAttribute('data-marker') || '' });
          self.mapEl.classList.add('is-map-ready');
        });
      }).catch(function () { self.mapFailed(); });
    }
    /* No map could be shown: the frame is taken away and the store card (with its directions link) stays. */
    mapFailed() {
      if (!this.mapEl) return;
      this.mapEl.classList.add('mxf-map--empty', 'is-map-failed');
      if (this.frameEl) this.frameEl.hidden = true;
      this.mapEl.classList.remove('is-stacked');
      /* with no store card either, nothing is left to show: the whole map area goes */
      if (!this.storeEl) {
        var area = this.mapEl.closest('[data-mxf-area]');
        if (area) area.hidden = true;
        this.classList.add('mxf--no-map');
      }
    }

    /* ------------------------------------------------------------ entrance animation */
    initEntrance() {
      var self = this;
      var enabled = this.dataset.anim === 'true' && 'IntersectionObserver' in window && !reduceMotion.matches;
      /* Theme Editor: every setting change re-renders the section. Play the entrance on first load and
         when an animation setting changed, not while text or colours are being edited. */
      if (window.Shopify && window.Shopify.designMode) {
        var seen = window.__mxfAnimKeys || (window.__mxfAnimKeys = {});
        var key = this.getAttribute('data-anim-key') || '';
        if (seen[this.id] === key) enabled = false;
        seen[this.id] = key;
        if (enabled) this.classList.add('mxf-anim');
      } else if (!this.classList.contains('mxf-anim')) {
        /* not armed before first paint (or disarmed by the failsafe): leave everything visible */
        enabled = false;
      }
      if (!enabled) { this.classList.remove('mxf-anim', 'is-entered', 'is-settled'); return; }
      var replay = this.dataset.animReplay === 'replay';
      this.entranceObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        if (entry.isIntersecting) {
          self.enter();
          if (!replay) { self.entranceObserver.disconnect(); self.entranceObserver = null; }
        } else if (replay && entry.intersectionRatio === 0) {
          clearTimeout(self.timers.settle);
          self.classList.remove('is-entered', 'is-settled');
        }
      }, { threshold: [0, 0.06] });
      this.entranceObserver.observe(this);
    }
    enter() {
      var self = this;
      if (this.classList.contains('is-entered')) return;
      this.classList.remove('is-settled');
      void this.offsetWidth;
      this.classList.add('is-entered');
      var total = ms(this, '--mxf-anim-dur', 650) + ms(this, '--mxf-anim-stagger', 70) * 9 + 80;
      this.timer('settle', function () { if (self.classList.contains('is-entered')) self.classList.add('is-settled'); }, total);
    }

    /* ------------------------------------------------------------ Theme Editor */
    initEditor() {
      var self = this;
      if (!(window.Shopify && window.Shopify.designMode)) return;
      this.listen(this, 'shopify:block:select', function (e) {
        var target = e.target;
        qsa('.is-editor-selected', self).forEach(function (el) { el.classList.remove('is-editor-selected'); });
        if (!target || !target.classList) return;
        target.classList.add('is-editor-selected');
        /* a Menu block on a phone-sized preview: open its accordion so the links can be seen */
        if (target.matches('[data-mxf-menu]')) self.setMenu(target, true);
      });
      this.listen(this, 'shopify:block:deselect', function (e) {
        if (e.target && e.target.classList) e.target.classList.remove('is-editor-selected');
      });
    }
  }

  customElements.define('mx-footer', MXFooter);
})();
